// The sandbox manager refuses work with 507 and a `sandbox_capacity_exceeded`
// body when no execution slots are free. Backend surfaces carry the same
// identifier (node errors, plugin last_error_code, 409 install refusals), so
// one place turns it into copy with the numbers and a Platform Status link.
export const SANDBOX_CAPACITY_IDENTIFIER = "sandbox_capacity_exceeded";
export const SANDBOX_STATUS_PATH = "/settings/platform/system?tab=sandbox";

export type SandboxCapacityParams = {
  requested_slots?: number | string | null;
  slots_free?: number | string | null;
  slots_total?: number | string | null;
  max_admissible_slots?: number | string | null;
  persistent_slots?: number | string | null;
  queue_depth?: number | string | null;
  retry_after?: number | string | null;
  reserved_slots?: number | string | null;
};

type ErrorLike = {
  status?: number;
  response?: { status?: number; data?: unknown };
  data?: unknown;
  error?: unknown;
  body?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** The capacity payload inside an API error, whichever client wrapped it. */
export function sandboxCapacityParams(
  error: unknown
): SandboxCapacityParams | null {
  const err = error as ErrorLike | null;
  for (const candidate of [
    err?.response?.data,
    err?.data,
    err?.error,
    err?.body,
  ]) {
    const record = asRecord(candidate);
    if (!record) continue;
    const detail = asRecord(record.detail);
    if (detail?.code === SANDBOX_CAPACITY_IDENTIFIER) {
      return detail as SandboxCapacityParams;
    }
    if (record.identifier === SANDBOX_CAPACITY_IDENTIFIER) {
      return (asRecord(record.params) ?? record) as SandboxCapacityParams;
    }
    if (record.code === SANDBOX_CAPACITY_IDENTIFIER) {
      return record as SandboxCapacityParams;
    }
  }
  return null;
}

export function isSandboxCapacityError(error: unknown): boolean {
  return sandboxCapacityParams(error) !== null;
}

function num(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** "needs 4 slots, 1 free of 8 (2 held by persistent sandboxes)" */
export function sandboxCapacitySummary(params: SandboxCapacityParams): string {
  const requested = num(params.requested_slots);
  const free = num(params.slots_free);
  const total = num(params.slots_total);
  if (requested === null || free === null || total === null) {
    return "no execution slots are available";
  }
  let text = `needs ${requested} slot${requested === 1 ? "" : "s"}, ${free} free of ${total}`;
  const notes: string[] = [];
  const reserved = num(params.reserved_slots);
  if (reserved) notes.push(`${reserved} reserved for workflows`);
  const persistent = num(params.persistent_slots);
  if (persistent) notes.push(`${persistent} held by persistent sandboxes`);
  const queued = num(params.queue_depth);
  if (queued) notes.push(`${queued} queued`);
  if (notes.length) text += ` (${notes.join(", ")})`;
  return text;
}

function neverFits(params: SandboxCapacityParams): number | null {
  const requested = num(params.requested_slots);
  const admissible = num(params.max_admissible_slots);
  return requested !== null && admissible !== null && requested > admissible
    ? admissible
    : null;
}

export function sandboxCapacityMessage(params: SandboxCapacityParams): string {
  const admissible = neverFits(params);
  if (admissible !== null) {
    return `Sandbox capacity exceeded (${sandboxCapacitySummary(params)}). No sandbox manager can admit more than ${admissible} slots: lower the requested execution slots or add sandbox capacity (see System → Sandbox).`;
  }
  return `Sandbox capacity exhausted (${sandboxCapacitySummary(params)}). Retry once capacity frees up, or check System → Sandbox to see which sandboxes hold the slots.`;
}

export function sandboxCapacityMarkdown(params: SandboxCapacityParams): string {
  const admissible = neverFits(params);
  if (admissible !== null) {
    return `**Sandbox capacity exceeded.** This ${sandboxCapacitySummary(params)}.\n\nNo sandbox manager can admit more than **${admissible}** slots: lower the node's execution slots or add sandbox capacity; [Sandbox status](${SANDBOX_STATUS_PATH}) shows what holds them.`;
  }
  return `**Sandbox capacity exhausted.** This ${sandboxCapacitySummary(params)}.\n\nRetry once capacity frees up, reduce how many code executions run in parallel, or open [Sandbox status](${SANDBOX_STATUS_PATH}) to see which sandboxes hold the slots.`;
}
